import { Controller } from '@hotwired/stimulus'

// Accessible hero carousel: auto-advance (paused on hover/focus and when the user
// prefers reduced motion), prev/next, dot indicators, and arrow-key support.
export default class extends Controller<HTMLElement> {
  static targets = ['slide', 'dot']
  static values = { interval: { type: Number, default: 6000 } }

  declare readonly slideTargets: HTMLElement[]
  declare readonly dotTargets: HTMLElement[]
  declare readonly intervalValue: number

  index = 0
  timer: number | undefined

  connect(): void {
    this.show(0)
    this.start()
  }

  disconnect(): void {
    this.stop()
  }

  next(): void {
    this.show((this.index + 1) % this.slideTargets.length)
  }

  prev(): void {
    this.show((this.index - 1 + this.slideTargets.length) % this.slideTargets.length)
  }

  goto(event: Event): void {
    const target = event.currentTarget as HTMLElement
    this.show(Number(target.dataset.index))
  }

  keydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowRight') { this.next(); this.restart() }
    if (event.key === 'ArrowLeft') { this.prev(); this.restart() }
  }

  start(): void {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    this.timer = window.setInterval(() => this.next(), this.intervalValue)
  }

  stop(): void {
    if (this.timer) window.clearInterval(this.timer)
    this.timer = undefined
  }

  restart(): void {
    this.stop()
    this.start()
  }

  private show(i: number): void {
    this.index = i
    this.slideTargets.forEach((slide, n) => {
      const active = n === i
      slide.style.opacity = active ? '1' : '0'
      slide.setAttribute('aria-hidden', String(!active))
    })
    this.dotTargets.forEach((dot, n) => {
      const active = n === i
      dot.setAttribute('aria-current', String(active))
      dot.style.opacity = active ? '1' : '0.4'
    })
  }
}
